/**
 * Phase 2B.3 — Bulk Mode canvas (productivity surface).
 *
 *   B1 — Bulk operations (7): weight, difficulty, competencies, section move,
 *        duplicate, delete, tags. Contextual action bar appears when ≥ 1
 *        content item is checked.
 *   B2 — Filter architecture: filter pills above the table for missing
 *        competencies, missing correct answers, validation errors, difficulty,
 *        tags, section, asset type. Filters compose with multi-select so
 *        bulk ops work on the *filtered* selection.
 *   B3 — Future-proofing: action bar reserves slots for Find & Replace and
 *        AI bulk ops (Rewrite, Translate, Simplify language, Increase
 *        difficulty, Remove biased wording). The reserved slots render with
 *        a disabled chip + tooltip "Available in Phase 9" so the UX intent
 *        stays visible without shipping the implementation.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Trash2, Copy, MoveRight, Sparkles, Search, Replace, Wand2, Filter,
  X, AlertTriangle, FileText, Layers, Tag, Plus, ChevronDown, Lock, Check,
} from 'lucide-react';
import { useDataLabel } from '@/lib/dataI18n';
import { DifficultyPicker } from '@/components/studio/DifficultyPicker';
import { Label } from '@/components/ui/label';

/* ============================================================================
 *                  FILTER MODEL (B2)
 * ========================================================================== */
//   {
//     section_id: string | 'all',
//     difficulty: number | 'all',
//     tag: string | 'all',
//     missing_competencies: boolean,
//     missing_correct_answers: boolean,
//     validation_errors: boolean,   // wired in Phase 2C when validator lights up
//   }
export const EMPTY_FILTERS = {
  section_id: 'all',
  difficulty: 'all',
  tag: 'all',
  missing_competencies: false,
  missing_correct_answers: false,
  validation_errors: false,
};

export function applyFilters(questions, filters, validationByQid = {}) {
  if (!filters) return questions;
  return questions.filter((q) => {
    if (filters.section_id !== 'all' && q.section_id !== filters.section_id) return false;
    if (filters.difficulty !== 'all' && Number(q.difficulty) !== Number(filters.difficulty)) return false;
    if (filters.tag !== 'all' && !(q.tags || []).includes(filters.tag)) return false;
    if (filters.missing_competencies && Object.keys(q.competency_map || {}).length > 0) return false;
    if (filters.missing_correct_answers) {
      const expectsCorrect = ['single_choice', 'multi_choice'].includes(q.type);
      const hasCorrect = (q.options || []).some((o) => o.is_correct);
      if (!expectsCorrect || hasCorrect) return false;
    }
    if (filters.validation_errors && !validationByQid[q.id]) return false;
    return true;
  });
}

/* ============================================================================
 *                          BULK MODE CANVAS
 * ========================================================================== */
export function CanvasBulkMode({
  sections, questions, editable, patchQuestions,
  competencies = [], availableTags = [], onBulkApplied, onOpenFindReplace,
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(() => new Set());
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [dialog, setDialog] = useState(null);     // { kind: 'weight'|'difficulty'|… }

  const filteredQuestions = useMemo(
    () => applyFilters(questions, filters),
    [questions, filters],
  );
  const filteredIds = useMemo(() => filteredQuestions.map((q) => q.id), [filteredQuestions]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const anySelected = selected.size > 0;
  const selectedCount = selected.size;

  const toggleOne = (qid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid); else next.add(qid);
      return next;
    });
  };
  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };
  const clearSelection = () => setSelected(new Set());

  /* ---------- Bulk operation appliers ---------- */
  const applyBulk = (mutator) => {
    if (!editable || !anySelected) return;
    const next = questions.map((q) => (selected.has(q.id) ? mutator(q) : q));
    patchQuestions(next);
  };
  const notifyBulk = (descriptor) => {
    if (onBulkApplied) {
      onBulkApplied({
        ...descriptor,
        count: selectedCount,
        entity_ids: Array.from(selected),
        priorSnapshot: { sections, questions },
      });
    }
  };
  const handleBulkDelete = () => {
    if (!editable || !anySelected) return;
    if (!window.confirm(t('studio.bulk.confirmDelete', {
      defaultValue: 'Delete {{n}} content item(s)? This cannot be undone yet (full undo arrives in Phase 2C).',
      n: selectedCount,
      count: selectedCount,
    }))) return;
    notifyBulk({ code: 'bulk_deleted' });
    patchQuestions(questions.filter((q) => !selected.has(q.id)));
    clearSelection();
  };
  const handleBulkDuplicate = () => {
    if (!editable || !anySelected) return;
    notifyBulk({ code: 'bulk_added' });
    const dupes = questions.filter((q) => selected.has(q.id)).map((q) => {
      // New id; backend mints fresh stable_id on next autosave.
      const { stable_id, id, ...rest } = q;
      void stable_id; void id;
      return {
        ...rest,
        id: `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`,
        prompt: `${q.prompt || ''} (copy)`,
      };
    });
    patchQuestions([...questions, ...dupes]);
  };
  const handleBulkMoveSection = (sectionId) => {
    notifyBulk({ code: 'bulk_moved_to_section', to: sectionId });
    applyBulk((q) => ({ ...q, section_id: sectionId }));
    setDialog(null);
  };
  const handleBulkWeight = (value) => {
    notifyBulk({ code: 'bulk_weight_set', to: Number(value) });
    applyBulk((q) => ({ ...q, weight: Number(value) }));
    setDialog(null);
  };
  const handleBulkDifficulty = (value) => {
    notifyBulk({ code: 'bulk_difficulty_set', to: Number(value) });
    applyBulk((q) => ({ ...q, difficulty: Number(value) }));
    setDialog(null);
  };
  const handleBulkCompetency = (code, weight) => {
    notifyBulk({ code: 'bulk_competency_added', to: code });
    const w = Number(weight);
    applyBulk((q) => ({
      ...q,
      competency_map: { ...(q.competency_map || {}), [code]: Number.isNaN(w) ? 1 : w },
    }));
    setDialog(null);
  };
  const handleBulkTags = (tags) => {
    notifyBulk({ code: 'bulk_tag_added', to: tags?.[0] });
    applyBulk((q) => ({ ...q, tags: Array.from(new Set([...(q.tags || []), ...tags])) }));
    setDialog(null);
  };

  return (
    <div className="p-6" data-testid="studio-canvas-bulk-mode">
      <BulkFiltersStrip
        sections={sections}
        questions={questions}
        availableTags={availableTags}
        filters={filters}
        setFilters={setFilters}
      />

      {anySelected && (
        <BulkActionBar
          editable={editable}
          selectedCount={selectedCount}
          totalShown={filteredIds.length}
          allFilteredSelected={allFilteredSelected}
          onSelectAllFiltered={toggleAll}
          onClear={clearSelection}
          onOpenWeight={() => setDialog({ kind: 'weight' })}
          onOpenDifficulty={() => setDialog({ kind: 'difficulty' })}
          onOpenCompetency={() => setDialog({ kind: 'competency' })}
          onOpenMoveSection={() => setDialog({ kind: 'move_section' })}
          onOpenTags={() => setDialog({ kind: 'tags' })}
          onDuplicate={handleBulkDuplicate}
          onDelete={handleBulkDelete}
          onOpenFindReplace={onOpenFindReplace}
        />
      )}

      <BulkTable
        questions={filteredQuestions}
        sections={sections}
        selected={selected}
        toggleOne={toggleOne}
        allFilteredSelected={allFilteredSelected}
        toggleAll={toggleAll}
      />

      {filteredQuestions.length === 0 && (
        <div className="text-center py-12 text-muted-foreground text-sm" data-testid="studio-bulk-empty">
          {t('studio.bulk.empty', { defaultValue: 'No content items match the current filters.' })}
        </div>
      )}

      {/* Per-operation dialogs */}
      {dialog?.kind === 'weight' && (
        <BulkValueDialog
          title={t('studio.bulk.setWeight', { defaultValue: 'Set weight on {{n}} items', n: selectedCount })}
          inputLabel={t('studio.bulk.weightLabel', { defaultValue: 'Weight' })}
          type="number"
          onApply={handleBulkWeight}
          onCancel={() => setDialog(null)}
          testid="studio-bulk-weight-dialog"
        />
      )}
      {dialog?.kind === 'difficulty' && (
        <BulkDifficultyDialog
          n={selectedCount}
          onApply={handleBulkDifficulty}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'competency' && (
        <BulkCompetencyDialog
          n={selectedCount}
          competencies={competencies}
          onApply={handleBulkCompetency}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'move_section' && (
        <BulkMoveSectionDialog
          n={selectedCount}
          sections={sections}
          onApply={handleBulkMoveSection}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'tags' && (
        <BulkTagsDialog
          n={selectedCount}
          availableTags={availableTags}
          onApply={handleBulkTags}
          onCancel={() => setDialog(null)}
        />
      )}
    </div>
  );
}

/* ============================================================================
 *                  FILTERS STRIP (B2)
 * ========================================================================== */
function BulkFiltersStrip({ sections, questions, availableTags, filters, setFilters }) {
  const { t } = useTranslation();
  const allTags = useMemo(() => {
    const s = new Set(availableTags);
    questions.forEach((q) => (q.tags || []).forEach((tg) => s.add(tg)));
    return Array.from(s).sort();
  }, [availableTags, questions]);

  const togglePill = (key) => setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  const reset = () => setFilters(EMPTY_FILTERS);
  const hasActive = (
    filters.section_id !== 'all' ||
    filters.difficulty !== 'all' ||
    filters.tag !== 'all' ||
    filters.missing_competencies ||
    filters.missing_correct_answers ||
    filters.validation_errors
  );

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="studio-bulk-filters">
      <Filter className="h-3.5 w-3.5 text-muted-foreground" />
      <Select value={filters.section_id} onValueChange={(v) => setFilters((p) => ({ ...p, section_id: v }))}>
        <SelectTrigger className="h-7 w-40 text-xs" data-testid="studio-bulk-filter-section">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('studio.bulk.filterAllSections', { defaultValue: 'All sections' })}</SelectItem>
          {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || t('studio.editor.untitledSection', { defaultValue: 'Untitled section' })}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={String(filters.difficulty)} onValueChange={(v) => setFilters((p) => ({ ...p, difficulty: v === 'all' ? 'all' : Number(v) }))}>
        <SelectTrigger className="h-7 w-32 text-xs" data-testid="studio-bulk-filter-difficulty">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('studio.bulk.filterAllDifficulty', { defaultValue: 'All difficulty' })}</SelectItem>
          {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>D{n}</SelectItem>)}
        </SelectContent>
      </Select>
      {allTags.length > 0 && (
        <Select value={filters.tag} onValueChange={(v) => setFilters((p) => ({ ...p, tag: v }))}>
          <SelectTrigger className="h-7 w-32 text-xs" data-testid="studio-bulk-filter-tag">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('studio.bulk.filterAllTags', { defaultValue: 'All tags' })}</SelectItem>
            {allTags.map((tg) => <SelectItem key={tg} value={tg}>{tg}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <FilterPill
        label={t('studio.bulk.filterMissingCompetencies', { defaultValue: 'Missing competencies' })}
        active={filters.missing_competencies}
        onClick={() => togglePill('missing_competencies')}
        testid="studio-bulk-filter-missing-competencies"
      />
      <FilterPill
        label={t('studio.bulk.filterMissingCorrect', { defaultValue: 'Missing correct answers' })}
        active={filters.missing_correct_answers}
        onClick={() => togglePill('missing_correct_answers')}
        testid="studio-bulk-filter-missing-correct"
      />
      <FilterPill
        label={t('studio.bulk.filterValidationErrors', { defaultValue: 'Validation errors' })}
        active={filters.validation_errors}
        onClick={() => togglePill('validation_errors')}
        testid="studio-bulk-filter-validation-errors"
        disabled
        hint={t('studio.bulk.validatorPhase', { defaultValue: 'Lights up in Phase 2C' })}
      />
      {hasActive && (
        <Button variant="ghost" size="sm" onClick={reset} className="h-7 px-2 text-xs" data-testid="studio-bulk-filter-reset">
          <X className="h-3 w-3 mr-1" />
          {t('studio.bulk.resetFilters', { defaultValue: 'Reset' })}
        </Button>
      )}
    </div>
  );
}

function FilterPill({ label, active, onClick, testid, disabled, hint }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      title={hint}
      className={[
        'h-7 px-2 text-xs rounded-full border transition-all gap-1 inline-flex items-center',
        active ? 'bg-primary text-primary-foreground border-primary' :
                 'bg-background text-foreground border-border hover:border-primary/40',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
      ].join(' ')}
    >
      {active && <span aria-hidden>✓</span>}
      {label}
      {disabled && <Lock className="h-2.5 w-2.5 ml-0.5 opacity-60" />}
    </button>
  );
}

/* ============================================================================
 *                  ACTION BAR (B1 + B3 reservation)
 * ========================================================================== */
function BulkActionBar({ editable, selectedCount, totalShown, allFilteredSelected, onSelectAllFiltered,
                        onClear, onOpenWeight, onOpenDifficulty, onOpenCompetency, onOpenMoveSection,
                        onOpenTags, onDuplicate, onDelete, onOpenFindReplace }) {
  const { t } = useTranslation();
  return (
    <div
      className="sticky top-2 z-10 mb-4 rounded-md bg-primary/10 border border-primary/30 backdrop-blur p-2 px-3 flex flex-wrap items-center gap-2"
      data-testid="studio-bulk-action-bar"
    >
      <Badge variant="default" className="text-xs" data-testid="studio-bulk-selected-count">
        {t('studio.bulk.selectedCount', { defaultValue: '{{n}} selected', n: selectedCount })}
      </Badge>
      <button type="button" onClick={onSelectAllFiltered} data-testid="studio-bulk-select-all-filtered" className="text-xs text-primary underline">
        {allFilteredSelected
          ? t('studio.bulk.clearFiltered', { defaultValue: 'Clear {{n}} shown', n: totalShown })
          : t('studio.bulk.selectAllFiltered', { defaultValue: 'Select all {{n}} shown', n: totalShown })}
      </button>
      <button type="button" onClick={onClear} className="text-xs text-muted-foreground underline" data-testid="studio-bulk-clear-selection">
        {t('studio.bulk.clearSelection', { defaultValue: 'Clear selection' })}
      </button>
      <div className="flex-1" />
      <BulkBtn icon={Sparkles} label={t('studio.bulk.weight', { defaultValue: 'Weight' })} onClick={onOpenWeight} disabled={!editable} testid="studio-bulk-action-weight" />
      <BulkBtn icon={Layers} label={t('studio.bulk.difficulty', { defaultValue: 'Difficulty' })} onClick={onOpenDifficulty} disabled={!editable} testid="studio-bulk-action-difficulty" />
      <BulkBtn icon={Plus} label={t('studio.bulk.competency', { defaultValue: 'Competency' })} onClick={onOpenCompetency} disabled={!editable} testid="studio-bulk-action-competency" />
      <BulkBtn icon={MoveRight} label={t('studio.bulk.moveSection', { defaultValue: 'Move to…' })} onClick={onOpenMoveSection} disabled={!editable} testid="studio-bulk-action-move" />
      <BulkBtn icon={Tag} label={t('studio.bulk.tags', { defaultValue: 'Tags' })} onClick={onOpenTags} disabled={!editable} testid="studio-bulk-action-tags" />
      <BulkBtn icon={Copy} label={t('studio.bulk.duplicate', { defaultValue: 'Duplicate' })} onClick={onDuplicate} disabled={!editable} testid="studio-bulk-action-duplicate" />
      <BulkBtn icon={Trash2} label={t('studio.bulk.delete', { defaultValue: 'Delete' })} onClick={onDelete} disabled={!editable} testid="studio-bulk-action-delete" variant="destructive" />
      {/* B3 — Future-reserved (Find & Replace + AI Bulk). */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" data-testid="studio-bulk-action-future">
            <Wand2 className="h-3 w-3" />
            {t('studio.bulk.moreActions', { defaultValue: 'More…' })}
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={() => onOpenFindReplace && onOpenFindReplace()}
            data-testid="studio-bulk-future-find-replace"
            className="text-xs cursor-pointer"
          >
            <Search className="h-3 w-3 mr-2" />
            {t('studio.bulk.findReplace', { defaultValue: 'Find & Replace' })}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs uppercase">
            {t('studio.bulk.reservedHeading', { defaultValue: 'Coming soon' })}
          </DropdownMenuLabel>
          <ReservedItem icon={Wand2} label={t('studio.bulk.aiRewrite', { defaultValue: 'AI · Rewrite selected' })} phase="9" testid="studio-bulk-future-ai-rewrite" />
          <ReservedItem icon={Wand2} label={t('studio.bulk.aiTranslate', { defaultValue: 'AI · Translate selected' })} phase="9" testid="studio-bulk-future-ai-translate" />
          <ReservedItem icon={Wand2} label={t('studio.bulk.aiSimplify', { defaultValue: 'AI · Simplify language' })} phase="9" testid="studio-bulk-future-ai-simplify" />
          <ReservedItem icon={Wand2} label={t('studio.bulk.aiHarder', { defaultValue: 'AI · Increase difficulty' })} phase="9" testid="studio-bulk-future-ai-harder" />
          <ReservedItem icon={Wand2} label={t('studio.bulk.aiBias', { defaultValue: 'AI · Remove biased wording' })} phase="9" testid="studio-bulk-future-ai-bias" />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function BulkBtn({ icon: Icon, label, onClick, disabled, testid, variant = 'outline' }) {
  return (
    <Button variant={variant} size="sm" onClick={onClick} disabled={disabled}
            data-testid={testid} className="h-7 gap-1 text-xs">
      <Icon className="h-3 w-3" /> {label}
    </Button>
  );
}

function ReservedItem({ icon: Icon, label, phase, testid }) {
  return (
    <DropdownMenuItem disabled data-testid={testid} className="gap-2">
      <Icon className="h-3 w-3" />
      <span className="flex-1">{label}</span>
      <Badge variant="outline" className="text-xs">Phase {phase}</Badge>
    </DropdownMenuItem>
  );
}

/* ============================================================================
 *                          BULK TABLE
 * ========================================================================== */
function BulkTable({ questions, sections, selected, toggleOne, allFilteredSelected, toggleAll }) {
  const { t } = useTranslation();
  const sectionById = useMemo(() => {
    const m = {};
    sections.forEach((s) => { m[s.id] = s; });
    return m;
  }, [sections]);

  return (
    <Table data-testid="studio-bulk-table" className="table-fixed w-full">
      <TableHeader>
        <TableRow>
          <TableHead className="w-8">
            <Checkbox
              checked={allFilteredSelected}
              onCheckedChange={toggleAll}
              data-testid="studio-bulk-select-all-checkbox"
              aria-label="Select all filtered"
            />
          </TableHead>
          <TableHead className="w-16">ID</TableHead>
          <TableHead className="w-28">{t('studio.bulk.col.section', { defaultValue: 'Section' })}</TableHead>
          <TableHead>{t('studio.bulk.col.prompt', { defaultValue: 'Prompt' })}</TableHead>
          <TableHead className="w-24">{t('studio.bulk.col.type', { defaultValue: 'Type' })}</TableHead>
          <TableHead className="w-16 text-right" title={t('studio.bulk.difficulty', { defaultValue: 'Difficulty' })}>D</TableHead>
          <TableHead className="w-16 text-right" title={t('studio.bulk.weight', { defaultValue: 'Weight' })}>{t('studio.bulk.col.weightShort', { defaultValue: 'W' })}</TableHead>
          <TableHead className="w-20">{t('studio.bulk.col.comp', { defaultValue: 'Comp.' })}</TableHead>
          <TableHead className="w-16 text-center">{t('studio.bulk.col.flags', { defaultValue: 'Flags' })}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {questions.map((q) => {
          const isSelected = selected.has(q.id);
          const section = sectionById[q.section_id];
          const expectsCorrect = ['single_choice', 'multi_choice'].includes(q.type);
          const hasCorrect = (q.options || []).some((o) => o.is_correct);
          const missingComp = Object.keys(q.competency_map || {}).length === 0;
          return (
            <TableRow
              key={q.id}
              data-testid={`studio-bulk-row-${q.id}`}
              data-selected={isSelected ? 'true' : 'false'}
              className={isSelected ? 'bg-primary/5' : ''}
            >
              <TableCell>
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleOne(q.id)}
                  data-testid={`studio-bulk-checkbox-${q.id}`}
                />
              </TableCell>
              <TableCell>
                {q.stable_id ? (
                  <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-muted">
                    {q.stable_id}
                  </span>
                ) : <span className="text-muted-foreground">—</span>}
              </TableCell>
              <TableCell>
                <span className="text-xs truncate inline-block max-w-[100px]">
                  {section?.name || '—'}
                </span>
              </TableCell>
              <TableCell>
                <span className="text-sm">
                  <FileText className="h-3 w-3 inline mr-1 text-muted-foreground" />
                  {q.prompt || <span className="text-muted-foreground italic">{t('studio.editor.untitledQuestion', { defaultValue: 'Untitled content' })}</span>}
                </span>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs" title={q.type || 'text'}>
                  {t(`studio.contentTypes.${q.type || 'text'}`, { defaultValue: q.type || 'text' })}
                </Badge>
              </TableCell>
              <TableCell className="text-right tabular-nums">{q.difficulty ?? '—'}</TableCell>
              <TableCell className="text-right tabular-nums">{q.weight ?? '—'}</TableCell>
              <TableCell>
                <span className="text-xs">{Object.keys(q.competency_map || {}).length}</span>
              </TableCell>
              <TableCell className="text-center">
                {(missingComp || (expectsCorrect && !hasCorrect)) && (
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 inline" data-testid={`studio-bulk-flag-${q.id}`} />
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/* ============================================================================
 *                          DIALOGS
 * ========================================================================== */
function BulkValueDialog({ title, inputLabel, type, onApply, onCancel, testid }) {
  const [value, setValue] = useState('');
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-sm" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div>
          <Label>{inputLabel}</Label>
          <Input
            type={type || 'text'}
            value={value}
            autoFocus
            onChange={(e) => setValue(e.target.value)}
            data-testid={`${testid}-input`}
            className="mt-2"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={() => onApply(value)} disabled={value === ''} data-testid={`${testid}-apply`}>
            {t('studio.bulk.apply', { defaultValue: 'Apply' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkDifficultyDialog({ n, onApply, onCancel }) {
  const { t } = useTranslation();
  const [d, setD] = useState(3);
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-md" data-testid="studio-bulk-difficulty-dialog">
        <DialogHeader>
          <DialogTitle>{t('studio.bulk.setDifficulty', { defaultValue: 'Set difficulty on {{n}} items', n })}</DialogTitle>
        </DialogHeader>
        <DifficultyPicker value={d} onChange={setD} />
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={() => onApply(d)} data-testid="studio-bulk-difficulty-apply">
            {t('studio.bulk.apply', { defaultValue: 'Apply' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkCompetencyDialog({ n, competencies, onApply, onCancel }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [weight, setWeight] = useState('1');
  const [search, setSearch] = useState('');
  const tData = useDataLabel();
  const wNum = weight === '' ? NaN : Number(weight);
  const weightInvalid = Number.isNaN(wNum) || wNum < 0 || wNum > 1;
  const dispName = (c) => t(`reports.competencies.${c.code}`, { defaultValue: tData(c.name) || c.code });
  const filtered = (competencies || []).filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return dispName(c).toLowerCase().includes(q)
      || (c.name || '').toLowerCase().includes(q)
      || (c.code || '').toLowerCase().includes(q)
      || (tData(c.category) || c.category || '').toLowerCase().includes(q);
  });
  const selected = (competencies || []).find((c) => c.code === code);
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-md overflow-x-hidden" data-testid="studio-bulk-competency-dialog">
        <DialogHeader>
          <DialogTitle>{t('studio.bulk.assignCompetency', { defaultValue: 'Assign competency to {{n}} items', n })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('studio.bulk.competencyLabel', { defaultValue: 'Competency' })}</Label>
            {competencies?.length ? (
              <div className="mt-1.5 space-y-1.5">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('studio.bulk.searchCompetencyPh', { defaultValue: 'Search competency…' })}
                  data-testid="studio-bulk-competency-search"
                />
                <div className="max-h-48 overflow-y-auto rounded-md border border-border divide-y divide-border/50" data-testid="studio-bulk-competency-list">
                  {filtered.length === 0 && (
                    <p className="text-xs text-muted-foreground px-3 py-2.5">
                      {t('studio.bulk.noCompetencyMatches', { defaultValue: 'No competencies match your search.' })}
                    </p>
                  )}
                  {filtered.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => setCode(c.code)}
                      title={c.code}
                      className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-muted/60 transition-colors ${code === c.code ? 'bg-primary/10' : ''}`}
                      data-testid={`studio-bulk-competency-option-${c.code}`}
                    >
                      {code === c.code && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                      <span className="flex-1 truncate">{dispName(c)}</span>
                      {c.category && (
                        <Badge variant="outline" className="text-xs shrink-0">
                          {(() => { const cl = tData(c.category); return cl === c.category ? c.category.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()) : cl; })()}
                        </Badge>
                      )}
                    </button>
                  ))}
                </div>
                {selected && (
                  <p className="text-xs text-muted-foreground" data-testid="studio-bulk-competency-selected">
                    {dispName(selected)} <span className="font-mono">({selected.code})</span>
                  </p>
                )}
              </div>
            ) : (
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. strategic_thinking" className="mt-1.5" data-testid="studio-bulk-competency-input" />
            )}
          </div>
          <div>
            <Label>{t('studio.bulk.weightLabel', { defaultValue: 'Weight' })}</Label>
            <Input
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className={`mt-1.5 ${weightInvalid ? 'border-destructive focus-visible:ring-destructive' : ''}`}
              data-testid="studio-bulk-competency-weight"
            />
            {weightInvalid ? (
              <p className="text-xs text-destructive mt-1" data-testid="studio-bulk-competency-weight-error">
                {t('studio.bulk.weightRangeError', { defaultValue: 'Weight must be a number between 0 and 1.' })}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                {t('studio.bulk.weightRangeHint', { defaultValue: 'Allowed range: 0 to 1 (relative influence of this item on the competency score).' })}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button disabled={!code || weightInvalid} onClick={() => onApply(code, weight)} data-testid="studio-bulk-competency-apply">
            {t('studio.bulk.apply', { defaultValue: 'Apply' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkMoveSectionDialog({ n, sections, onApply, onCancel }) {
  const { t } = useTranslation();
  const [sid, setSid] = useState('');
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-md" data-testid="studio-bulk-move-dialog">
        <DialogHeader>
          <DialogTitle>{t('studio.bulk.moveTo', { defaultValue: 'Move {{n}} items to section', n, count: n })}</DialogTitle>
        </DialogHeader>
        <Select value={sid} onValueChange={setSid}>
          <SelectTrigger data-testid="studio-bulk-move-select">
            <SelectValue placeholder={t('studio.bulk.selectSectionPh', { defaultValue: 'Select section…' })} />
          </SelectTrigger>
          <SelectContent>
            {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || t('studio.editor.untitledSection', { defaultValue: 'Untitled section' })}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button disabled={!sid} onClick={() => onApply(sid)} data-testid="studio-bulk-move-apply">
            {t('studio.bulk.apply', { defaultValue: 'Apply' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkTagsDialog({ n, availableTags, onApply, onCancel }) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [picked, setPicked] = useState([]);
  const togglePicked = (tg) => setPicked((p) => p.includes(tg) ? p.filter((x) => x !== tg) : [...p, tg]);
  const handleApply = () => {
    const fromInput = input.split(',').map((s) => s.trim()).filter(Boolean);
    onApply(Array.from(new Set([...picked, ...fromInput])));
  };
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-md" data-testid="studio-bulk-tags-dialog">
        <DialogHeader>
          <DialogTitle>{t('studio.bulk.addTags', { defaultValue: 'Add tags to {{n}} items', n })}</DialogTitle>
        </DialogHeader>
        {availableTags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {availableTags.map((tg) => (
              <button key={tg} type="button" onClick={() => togglePicked(tg)}
                      className={`text-xs px-2 py-0.5 rounded-full border ${picked.includes(tg) ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary/40'}`}>
                {tg}
              </button>
            ))}
          </div>
        )}
        <div className="mt-2">
          <Label>{t('studio.bulk.newTags', { defaultValue: 'New tags (comma-separated)' })}</Label>
          <Input value={input} onChange={(e) => setInput(e.target.value)} className="mt-1.5" data-testid="studio-bulk-tags-input" placeholder="urgent, sprintA, easy" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={handleApply} disabled={picked.length === 0 && !input.trim()} data-testid="studio-bulk-tags-apply">
            {t('studio.bulk.apply', { defaultValue: 'Apply' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
